import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./dashboard.scss";
import "../../assets/css/dashboard.scss";
import {
  TotalProjects,
  ActiveProjects,
  TotalWorkers,
  FilterButton,
} from "../../assets/icons";
import { getDashCount, getWorkersData, getDashboardData, getSubscriptionStats } from "../../services/service";
import { Dropdown, Menu } from "antd";
import FilterMenu from "../common/FilterMenu";
import LineGraph from "./LineGraph";
import { Checkbox } from "@mui/material";
import PieChart from "./PieChart";
import { useNavigate } from "react-router-dom";
import { getProjectColor } from "../../utils/colors";
import { useSelector } from "react-redux";
import { selectCurrentPlan } from "../../redux/selectors/authSelectors";
import UpgradePromptModal from "../common/UpgradePromptModal";
import { FEATURE_REQUIRED_PLANS } from '../../utils/planFeatureAccess';
import { Spin } from "antd";
import SuperAdminRevenueChart from "./SuperAdminRevenueChart";

const DashboardCom = () => {
  const [cardData, setCardData] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [selectedProject, setSelectedProject] = useState("");
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const currentPlan = useSelector(selectCurrentPlan);
  const [loader, setLoader] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [subscriptionStats, setSubscriptionStats] = useState(null);
  const [subscriptionStatsLoading, setSubscriptionStatsLoading] = useState(false);

  // Check if user is super admin
  useEffect(() => {
    const userDataString = localStorage.getItem('userData');
    if (userDataString) {
      try {
        const userData = JSON.parse(userDataString);
        setIsSuperAdmin(userData?.user?.user?.role?.toLowerCase() === 'super_admin');
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  useEffect(() => {
    const fetchSubscriptionStats = async () => {
      setSubscriptionStatsLoading(true);
      try {
        const response = await getSubscriptionStats();
        if (response?.success) {
          setSubscriptionStats(response.data || {});
        }
      } catch (error) {
        console.error("Error fetching subscription stats:", error);
      }
      setSubscriptionStatsLoading(false);
    };

    if (isSuperAdmin) {
      fetchSubscriptionStats();
    }
  }, [isSuperAdmin]);
  const handlegetData = async () => {
    setLoader(true);
    try {
      const response = await getDashboardData();
      if (response?.success) {
        setLoader(false);
        setDashboardData(response?.data);
        
        // Transform project expense data for LineGraph with consistent random colors
        const projectsWithColors = response?.data?.projectsExpense?.projectsData?.map((project) => ({
          id: project.id,
          name: project.name,
          selected: true,
          type: "line",
          data: project.monthlyData,
          color: getProjectColor(project.id), // Use project ID for consistent colors
        }));
        
        setProjects([
          { id: "all", name: "All", selected: true },
          ...projectsWithColors
        ]);
      }
    } catch (error) {
      setLoader(false);
      console.error("Error fetching dashboard data:", error);
    }
  };

  useEffect(() => {
    handlegetData();
  }, []);

  const data1 = [
    {
      id: 1,
      text: "Total Projects",
      total: dashboardData?.total_projects || 0,
      icon: TotalProjects,
      route: "/view-project",
    },
    {
      id: 2,
      text: "Active Projects",
      total: dashboardData?.total_active_projects || 0,
      icon: ActiveProjects,
      route: "/view-project?status=active",
    },
    {
      id: 3,
      text: "Workers",
      total: dashboardData?.total_workers || 0,
      icon: TotalWorkers,
      route: "/view-worker",
    },
  ];

  const [dropdownValueChange, setDropdownValueChange] = useState("");

  const fetchWorkers = async (projectId = "") => {
    try {
      const response = await getWorkersData(projectId, 10, 1, "");
      if (response?.success) {
        setWorkers(response?.data || []);
      }
    } catch (error) {
      console.error("Error fetching workers:", error);
    }
  };

  useEffect(() => {
    const fetchedProjects = [
      { id: "all", name: "All", selected: true },
      {
        id: "buildpro",
        name: "BuildPro Solutions",
        selected: true,
        type: "line",
        data: [50, 75, 100, 125, 150, 200, 180, 220, 250, 230, 190, 170],
      },

      {
        id: "primebuild",
        name: "PrimeBuild Systems",
        selected: true,
        type: "line",
        data: [25, 50, 75, 100, 125, 150, 200, 180, 150, 130, 110, 90],
      },
      {
        id: "nextlevel",
        name: "NextLevel Structures",
        selected: true,
        type: "line",
        data: [0, 20, 40, 60, 80, 100, 120, 140, 160, 180, 200, 220],
      },
      {
        id: "buildwise",
        name: "BuildWise Innovations",
        selected: true,
        type: "line",
        data: [100, 120, 140, 160, 180, 200, 220, 240, 260, 280, 300, 320],
      },
      {
        id: "structo",
        name: "Structo Solutions",
        selected: true,
        type: "line",
        data: [0, 20, 40, 60, 80, 100, 120, 140, 160, 180, 200, 220],
      },
      {
        id: "masterbuild",
        name: "MasterBuild Experts",
        selected: true,
        type: "line",
        data: [0, 33, 45, 67, 87, 109, 127, 146, 168, 183, 209, 228],
      },
      {
        id: "futureform",
        name: "FutureForm Builders",
        selected: true,
        type: "line",
        data: [0, 30, 45, 65, 87, 107, 123, 145, 163, 185, 201, 250],
      },
    ];
    const projectsWithColors = fetchedProjects?.map((project) => ({
      ...project,
      color: project.id === "all" ? null : getProjectColor(project.id),
    }));

    setProjects(projectsWithColors);
    fetchWorkers();
  }, []);

  const handleReset = () => {
    setProjects((prevProjects) =>
      prevProjects.map((project) => ({
        ...project,
        selected: true,
      }))
    );
  };

  const handleCheckboxChange = (id) => {
    setProjects((prevProjects) => {
      if (id === "all") {
        return prevProjects.map((project) => ({
          ...project,
          selected: true,
        }));
      }

      return prevProjects.map((project) => {
        if (project.id === "all") {
          return { ...project, selected: false };
        }
        return project.id === id
          ? { ...project, selected: !project.selected }
          : project;
      });
    });
  };

  const handleChangeSelect = (value) => {
    setDropdownValueChange(value);
  };

  // Add this helper function to get initials
  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Add state for dropdown visibility
  const [dropdownVisible, setDropdownVisible] = useState(false);

  // Add state for tracking applied filters
  const [appliedFilters, setAppliedFilters] = useState({
    project: ''
  });

  // Add helper function to count active filters
  const getActiveFilterCount = () => {
    let count = 0;
    if (appliedFilters.project) count++;
    return count;
  };

  // Update getMenu function
  const getMenu = () => (
    <Menu>
      <FilterMenu 
        workerFilter={true} 
        handleSearchChange={(values) => {
          setSelectedProject(values.project_id);
          fetchWorkers(values.project_id);
          setDropdownVisible(false);
          // Update applied filters when filter is applied
          setAppliedFilters({
            project: values.project_id
          });
        }}
        setDropdownVisible={setDropdownVisible}
      />
    </Menu>
  );

  const handleWorkerClick = (workerId) => {
    navigate(`/worker-details/${workerId}`);
  };

  const isAdvancedReportingAccessible = () => {
    const planLevels = {
      'trial': 0,
      'basic': 1,
      'premium': 2,
      'ultimate': 3
    };
    return planLevels[currentPlan] >= planLevels[FEATURE_REQUIRED_PLANS.advancedReporting];
  };

  // Add this to handle stats section clicks
  const handleStatsClick = (e) => {
    if (!isAdvancedReportingAccessible()) {
      e.preventDefault();
      setShowUpgradeModal(true);
      return;
    }
    // Normal stats handling logic
  };

  return (
   loader?<div className="w-full h-screen flex justify-center items-center"> <Spin /></div>: <>
      <div className="w-full flex justify-center items-center flex-col gap-4 py-6">
        <div className="w-full flex items-center justify-between px-3 pb-4 -mt-2">
          <p className="font-semibold text-[#1E3A8A] text-[20px]">Home</p>
        </div>
        <div className="w-full grid grid-cols-3 -mt-3">
          {data1?.map(({ text, total, icon, route }) => {
            return (
              <div className="px-3 w-full">
                <Link to={`${route}`}>
                  <div className="rounded-[8px] w-full h-[100px] bg-white flex items-center justify-between px-4">
                    <div className="dashboard_icon">{icon}</div>
                    <div className="flex flex-col items-end">
                      <p className="font-medium !text-[28px] !text-[#4B5563]">
                        {total}
                      </p>
                      <p className="font-medium !text-[14px] !text-[#B7B7B7]">
                        {text}
                      </p>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
        <div className="w-full flex items-center px-3 gap-4">
          {isSuperAdmin && (
            <div className="w-full">
              <SuperAdminRevenueChart
                loading={subscriptionStatsLoading}
                monthlyData={
                  subscriptionStats?.monthlySummary ||
                  subscriptionStats?.monthlyRevenue ||
                  subscriptionStats?.monthlyRevenueData ||
                  []
                }
              />
            </div>
          )}
        </div>
        <div className="w-full flex items-center px-3 gap-4">
          <div className="w-[60%] h-[375px] bg-white rounded-[8px] py-3 px-3">
            <div className="w-full mb-3">
              <p className="text-lg font-bold text-[#1E3A8A]">
                Project Expenses
              </p>
            </div>
            <LineGraph projects={projects} />
          </div>
          <div className="w-[40%] h-[375px] flex flex-col gap-2 bg-white rounded-[8px] overflow-y-scroll">
            <div className="w-full flex items-center justify-between pt-3 px-3">
              <p className="text-base font-semibold text-[#1E3A8A]">
                Project Name
              </p>
              <button
                onClick={handleReset}
                className="w-[53px] h-[28px] rounded-[4px] bg-[#F97316] text-[13px] text-white font-medium hover:bg-orange-600 transition-colors"
              >
                Reset
              </button>
            </div>

            <div className="flex-1 px-3">
              <div className="space-y-0">
                {projects?.map((project) => (
                  <label
                    key={project?.id}
                    className="flex items-center space-x-3 !mb-0 cursor-pointer"
                  >
                    <Checkbox
                      id={project?.id}
                      checked={project?.selected}
                      onChange={() => handleCheckboxChange(project?.id)}
                      className="rounded-sm"
                    />
                    <div className="flex items-center gap-2">
                      {project?.id !== "all" && (
                        <div
                          className="w-[12px] h-[12px] rounded-full"
                          style={{ backgroundColor: project?.color }}
                        />
                      )}
                      <span className="text-sm text-gray-700">
                        {project?.name}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="w-full flex items-center px-3 gap-4">
          <div className="w-[60%] h-[448px] bg-white rounded-[8px] py-3 px-3">
            <div className="w-full mb-3">
              <p className="text-lg font-bold text-[#1E3A8A]">
                Project Workers Distribution
              </p>
            </div>
            <div className="w-full h-full px-4">
              <PieChart projectWorkers={dashboardData?.projectWorkers} />
            </div>
          </div>
          <div className="w-[40%] h-[448px] flex flex-col gap-2 bg-white rounded-[8px] overflow-y-scroll">
            <div className="w-full flex items-center justify-between px-3 pb-4 mt-3">
              <p className="font-semibold text-[#1E3A8A] text-base">Workers</p>
              <Dropdown
                overlay={getMenu()}
                trigger={["click"]}
                placement="bottomRight"
                visible={dropdownVisible}
                onVisibleChange={setDropdownVisible}
                getPopupContainer={() => document.body}
              >
                <span className="cursor-pointer relative">
                  {FilterButton}
                  {getActiveFilterCount() > 0 && (
                    <span className="absolute -top-2 -right-2 bg-[#F97316] text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
                      {getActiveFilterCount()}
                    </span>
                  )}
                </span>
              </Dropdown>
            </div>

            <div className="flex-1 px-3">
              {(!workers || workers.length === 0) ? (
                <div className="w-full py-8 text-center text-[#6D7482] text-sm">
                  No record found
                </div>
              ) : (
                <div className="space-y-4">
                  {workers?.map((worker) => (
                    <div 
                      key={worker.id}
                      onClick={() => handleWorkerClick(worker.id)}
                      className="cursor-pointer"
                    >
                      <div className="flex items-center cursor-pointer space-x-3">
                        <div className="w-[36px] h-[36px] rounded-full flex justify-center items-center bg-[#F97316] text-white">
                          {worker.profile_image ? (
                            <img 
                              src={worker.profile_image} 
                              alt={worker.username}
                              className="w-full h-full rounded-full object-cover"
                            />
                          ) : (
                            <span className="text-sm">
                              {getInitials(worker.username)}
                            </span>
                          )}
                        </div>
                        <div>
                          <p className="text-[14px] text-[#4B5563]">
                            {worker.username}
                          </p>
                        </div>
                      </div>
                      <div className="w-full h-[1px] bg-[#EFEFEF] mt-4"></div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

    

      {/* Upgrade Modal - Hidden for super admin */}
      {!isSuperAdmin && (
        <UpgradePromptModal
          show={showUpgradeModal}
          handleClose={() => setShowUpgradeModal(false)}
          featureName="Advanced Reporting"
          requiredPlan="Ultimate"
        />
      )}
    </>
  );
};

export default DashboardCom;
